import { Button, Modal } from "react-bootstrap";
import type { LicenciaEstado } from "../../services/licenciaService";
import "./LicenciaModal.css";

interface Props {
  licencia: LicenciaEstado | null;
  show: boolean;
  bloqueada: boolean;
  onClose: () => void;
}

const formatFecha = (value?: string | null) => {
  if (!value) return "No configurada";
  const [year, month, day] = value.slice(0, 10).split("-").map(Number);
  if (!year || !month || !day) return value;
  return new Intl.DateTimeFormat("es-PA", { dateStyle: "long" }).format(new Date(year, month - 1, day));
};

const formatDiasRestantes = (dias?: number | null) => {
  if (dias === null || dias === undefined) return "No disponible";
  const diasAbs = Math.abs(dias);
  const unidad = diasAbs === 1 ? "dia" : "dias";
  if (dias < 0) return `Vencio hace ${diasAbs} ${unidad}`;
  if (dias === 0) return "Vence hoy";
  return `Faltan ${dias} ${unidad}`;
};

const formatMonto = (licencia: LicenciaEstado | null) => {
  const monto = Number(licencia?.monto_renovacion);
  if (!Number.isFinite(monto) || monto <= 0) return null;
  return new Intl.NumberFormat("es-PA", {
    style: "currency",
    currency: licencia?.moneda || "USD"
  }).format(monto);
};

export const LicenciaModal = ({ licencia, show, bloqueada, onClose }: Props) => {
  const monto = formatMonto(licencia);
  const titulo = bloqueada ? "Licencia vencida" : "Renovacion de licencia";
  const mensaje = licencia?.mensaje || (
    bloqueada
      ? "La licencia del sistema vencio. Debe renovar la licencia para continuar usando la aplicacion."
      : "La licencia del sistema esta proxima a vencer. Gestione la renovacion para evitar la suspension del servicio."
  );

  return (
    <Modal
      show={show}
      onHide={bloqueada ? undefined : onClose}
      backdrop="static"
      keyboard={!bloqueada}
      centered
    >
      <Modal.Header closeButton={!bloqueada} className="license-modal__header">
        <span className={`license-modal__icon ${bloqueada ? "license-modal__icon--danger" : "license-modal__icon--warning"}`}>
          <i className={`bi ${bloqueada ? "bi-lock-fill" : "bi-exclamation-triangle-fill"}`}></i>
        </span>
        <Modal.Title>{titulo}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="license-modal__message">{mensaje}</p>
        <div className="license-modal__summary">
          <div className="license-modal__row">
            <span className="license-modal__label">Fecha de vencimiento</span>
            <span className="license-modal__value">{formatFecha(licencia?.fecha_vencimiento)}</span>
          </div>
          <div className="license-modal__row">
            <span className="license-modal__label">Estado</span>
            <span className="license-modal__value">{formatDiasRestantes(licencia?.dias_restantes)}</span>
          </div>
          {licencia?.cliente && (
            <div className="license-modal__row">
              <span className="license-modal__label">Cliente</span>
              <span className="license-modal__value">{licencia.cliente}</span>
            </div>
          )}
          {licencia?.plan && (
            <div className="license-modal__row">
              <span className="license-modal__label">Plan</span>
              <span className="license-modal__value">{licencia.plan}</span>
            </div>
          )}
          {monto && (
            <div className="license-modal__row">
              <span className="license-modal__label">Monto de renovacion</span>
              <span className="license-modal__value">{monto}</span>
            </div>
          )}
          {licencia?.contacto_renovacion && (
            <div className="license-modal__row">
              <span className="license-modal__label">Contacto</span>
              <span className="license-modal__value">{licencia.contacto_renovacion}</span>
            </div>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer>
        {bloqueada ? (
          <span className="license-modal__footer-text">Sistema bloqueado por vencimiento de licencia</span>
        ) : (
          <Button variant="primary" onClick={onClose}>
            <i className="bi bi-check2-circle me-2"></i>
            Entendido
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};
